import { useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/database/userService';

/**
 * Theme controls for interactive toggles. Applies the choice immediately
 * (ThemeContext + localStorage, the authoritative per-device store) AND
 * persists it best-effort to the signed-in user's Firestore doc so it is saved
 * for the user and can seed a fresh device (re-read at login by AuthContext).
 *
 * Use this for ANY user-facing theme switch (navbar, sidebar, admin, settings).
 * For read-only styling that just needs the current value, use `useTheme()`.
 */
export const useThemePreference = () => {
  const { isDarkMode, setDarkMode } = useTheme();
  const { currentUser } = useAuth();

  const setTheme = useCallback(
    (value: boolean) => {
      setDarkMode(value); // immediate + localStorage
      const uid = currentUser?.uid;
      if (uid) {
        userService
          .updateUser(uid, { preferences: { darkMode: value } })
          .catch(() => {
            /* keep the local change even if the write fails */
          });
      }
    },
    [setDarkMode, currentUser],
  );

  const toggleTheme = useCallback(() => setTheme(!isDarkMode), [setTheme, isDarkMode]);

  return { isDarkMode, toggleTheme, setTheme };
};
